import { Injectable, Inject, Optional, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError, of, Subject, timer } from 'rxjs';
import { catchError, tap, map, takeUntil, switchMap, filter } from 'rxjs/operators';

import { AUTH_CONFIG, AuthConfig, DEFAULT_AUTH_CONFIG } from '../auth.config';
import { TokenService } from './token.service';
import {
  User,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  RefreshTokenResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  ApiResponse,
  AuthState
} from '../models/auth.models';

/**
 * Auth Service
 * Handles authentication operations: login, logout, register, token refresh
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private config: AuthConfig;
  private destroy$ = new Subject<void>();

  // Auth state
  private authState$ = new BehaviorSubject<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null
  });

  // Current user
  private currentUser$ = new BehaviorSubject<User | null>(null);

  // Token refresh in progress
  private isRefreshing = false;
  private refreshTokenSubject$ = new BehaviorSubject<string | null>(null);

  constructor(
    private http: HttpClient,
    private router: Router,
    private tokenService: TokenService,
    @Optional() @Inject(AUTH_CONFIG) config: AuthConfig
  ) {
    this.config = config || DEFAULT_AUTH_CONFIG;
    this.initializeAuth();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ==================== Initialization ====================

  /**
   * Initialize authentication state on app start
   * Attempts to restore session from refresh token cookie
   */
  private initializeAuth(): void {
    // Check if we have tokens in memory (BehaviorSubject)
    const user = this.tokenService.getUser();
    const isAuthenticated = this.tokenService.isAuthenticated();

    if (isAuthenticated && user) {
      // Already authenticated in memory
      this.currentUser$.next(user);
      this.authState$.next({
        isAuthenticated: true,
        isLoading: false,
        user,
        error: null
      });

      // Setup auto token refresh
      if (this.config.enableAutoRefresh) {
        this.setupAutoRefresh();
      }
    } else if (this.tokenService.canRestoreSession()) {
      // Has refresh token in cookie - try to restore session
      this.restoreSession();
    } else {
      // No session to restore
      this.authState$.next({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null
      });
    }
  }

  /**
   * Restore session using refresh token from cookie
   * Called on page load/refresh when access token is not in memory
   */
  restoreSession(): Observable<boolean> {
    this.setLoading(true);

    return this.refreshToken().pipe(
      switchMap(response => {
        if (response.success && response.data?.access_token) {
          // Token refreshed, now get user profile
          return this.getUserProfile().pipe(
            map(profileResponse => {
              this.setLoading(false);
              return profileResponse.success;
            }),
            catchError(() => {
              this.setLoading(false);
              return of(false);
            })
          );
        }
        this.setLoading(false);
        return of(false);
      }),
      catchError(() => {
        this.setLoading(false);
        this.tokenService.clearSession();
        return of(false);
      })
    );
  }

  // ==================== Public Observables ====================

  /**
   * Get auth state as observable
   */
  get authState(): Observable<AuthState> {
    return this.authState$.asObservable();
  }

  /**
   * Get current user as observable
   */
  get currentUser(): Observable<User | null> {
    return this.currentUser$.asObservable();
  }

  /**
   * Get current user value
   */
  get user(): User | null {
    return this.currentUser$.getValue();
  }

  /**
   * Check if user is authenticated
   */
  get isAuthenticated(): boolean {
    return this.tokenService.isAuthenticated();
  }

  /**
   * Get access token
   */
  get accessToken(): string | null {
    return this.tokenService.getAccessToken();
  }

  // ==================== Authentication Methods ====================

  /**
   * Login user
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    this.setLoading(true);

    const url = this.getEndpoint(this.config.loginEndpoint);

    return this.http.post<LoginResponse>(url, credentials).pipe(
      tap(response => {
        if (response.success && response.data) {
          // Handle both object and array response from backend
          const authData = Array.isArray(response.data) ? response.data[0] : response.data;
          this.handleLoginSuccess(authData, credentials.remember_me);
        }
      }),
      catchError(error => this.handleError(error)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Register new user
   */
  register(userData: RegisterRequest): Observable<RegisterResponse> {
    this.setLoading(true);

    const url = this.getEndpoint(this.config.registerEndpoint);

    return this.http.post<RegisterResponse>(url, userData).pipe(
      tap(response => {
        if (response.success && response.data?.access_token) {
          this.handleLoginSuccess({
            user: response.data.user,
            access_token: response.data.access_token
          });
        }
      }),
      catchError(error => this.handleError(error)),
      tap(() => this.setLoading(false))
    );
  }


  logout(callApi: boolean = true): Observable<any> {
    if (callApi && this.tokenService.hasAccessToken()) {
      const url = this.getEndpoint(this.config.logoutEndpoint);

      return this.http.post(url, {}).pipe(
        catchError(() => of(null)), // Ignore logout API errors
        tap(() => this.handleLogout())
      );
    } else {
      this.handleLogout();
      return of(null);
    }
  }

  /**
   * Refresh access token
   */
  refreshToken(): Observable<RefreshTokenResponse> {
    if (this.isRefreshing) {
      // Wait for current refresh to complete
      return this.refreshTokenSubject$.pipe(
        filter(token => token !== null),
        map(token => ({ success: true, data: { access_token: token! } }))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject$.next(null);

    const refreshToken = this.tokenService.getRefreshToken();
    const url = this.getEndpoint(this.config.refreshTokenEndpoint);

    return this.http.post<RefreshTokenResponse>(url, { refresh_token: refreshToken }).pipe(
      tap(response => {
        this.isRefreshing = false;
        if (response.success && response.data) {
          this.tokenService.setAccessToken(response.data.access_token);
          if (response.data.refresh_token) {
            this.tokenService.setRefreshToken(response.data.refresh_token);
          }
          this.refreshTokenSubject$.next(response.data.access_token);
        }
      }),
      catchError(error => {
        this.isRefreshing = false;
        this.handleLogout();
        return throwError(() => error);
      })
    );
  }

  /**
   * Forgot password - send reset email
   */
  forgotPassword(data: ForgotPasswordRequest): Observable<ApiResponse> {
    const url = this.getEndpoint(this.config.forgotPasswordEndpoint);
    return this.http.post<ApiResponse>(url, data).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Reset password with token
   */
  resetPassword(data: ResetPasswordRequest): Observable<ApiResponse> {
    const url = this.getEndpoint(this.config.resetPasswordEndpoint);
    return this.http.post<ApiResponse>(url, data).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Change password (authenticated)
   */
  changePassword(data: ChangePasswordRequest): Observable<ApiResponse> {
    const url = this.getEndpoint(this.config.changePasswordEndpoint);
    return this.http.post<ApiResponse>(url, data).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Get user profile
   */
  getUserProfile(): Observable<ApiResponse<User>> {
    const url = this.getEndpoint(this.config.userProfileEndpoint);
    return this.http.get<ApiResponse<User>>(url).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.tokenService.setUser(response.data);
          this.currentUser$.next(response.data);
          this.updateAuthState({ user: response.data });
        }
      }),
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Update user profile
   */
  updateProfile(data: Partial<User>): Observable<ApiResponse<User>> {
    const url = this.getEndpoint(this.config.userProfileEndpoint);
    return this.http.put<ApiResponse<User>>(url, data).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.tokenService.setUser(response.data);
          this.currentUser$.next(response.data);
          this.updateAuthState({ user: response.data });
        }
      }),
      catchError(error => this.handleError(error))
    );
  }

  // ==================== Role & Permission Methods ====================

  /**
   * Check if user has a specific role
   */
  hasRole(role: string | string[]): boolean {
    const user = this.user;
    if (!user?.role_name) return false;

    const roles = Array.isArray(role) ? role : [role];
    return roles.some(r =>
      user.role_name?.toLowerCase() === r.toLowerCase()
    );
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: string | string[]): boolean {
    const user = this.user;
    if (!user?.permissions) return false;

    const perms = Array.isArray(permission) ? permission : [permission];
    return perms.some(p => user.permissions?.includes(p));
  }

  /**
   * Check if user has all specified permissions
   */
  hasAllPermissions(permissions: string[]): boolean {
    const user = this.user;
    if (!user?.permissions) return false;

    return permissions.every(p => user.permissions?.includes(p));
  }

  // ==================== Private Methods ====================

  /**
   * Handle successful login
   */
  private handleLoginSuccess(
    data: { user: User; access_token: string; refresh_token?: string; expires_in?: number },
    rememberMe?: boolean
  ): void {
    // Save to storage
    this.tokenService.saveSession({
      user: data.user,
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      remember_me: rememberMe
    });

    // Update state
    this.currentUser$.next(data.user);
    this.updateAuthState({
      isAuthenticated: true,
      user: data.user,
      error: null
    });

    // Setup auto refresh if enabled
    if (this.config.enableAutoRefresh) {
      this.setupAutoRefresh();
    }
  }

  /**
   * Handle logout
   */
  private handleLogout(): void {
    this.tokenService.clearSession();
    this.currentUser$.next(null);
    this.updateAuthState({
      isAuthenticated: false,
      user: null,
      error: null
    });

    // Navigate to login
    this.router.navigate([this.config.logoutRedirectUrl]);
  }

  /**
   * Setup automatic token refresh
   */
  private setupAutoRefresh(): void {
    const secondsUntilExpiry = this.tokenService.getSecondsUntilExpiry();
    if (secondsUntilExpiry <= 0) return;

    const refreshTime = Math.max(0, (secondsUntilExpiry - this.config.refreshTokenBeforeExpiry) * 1000);

    timer(refreshTime).pipe(
      takeUntil(this.destroy$),
      switchMap(() => {
        if (this.tokenService.hasRefreshToken()) {
          return this.refreshToken();
        }
        return of(null);
      })
    ).subscribe({
      next: () => {
        // Setup next refresh
        if (this.isAuthenticated) {
          this.setupAutoRefresh();
        }
      },
      error: () => {
        // Token refresh failed, logout
        this.handleLogout();
      }
    });
  }

  /**
   * Get full API endpoint URL
   */
  private getEndpoint(endpoint: string): string {
    if (endpoint.startsWith('http')) {
      return endpoint;
    }
    return `${this.config.apiBaseUrl}${endpoint}`;
  }

  /**
   * Update auth state
   */
  private updateAuthState(partialState: Partial<AuthState>): void {
    const currentState = this.authState$.getValue();
    this.authState$.next({ ...currentState, ...partialState });
  }

  /**
   * Set loading state
   */
  private setLoading(isLoading: boolean): void {
    this.updateAuthState({ isLoading });
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let message = 'An error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      message = error.error.message;
    } else {
      // Server-side error
      message = error.error?.message || error.message || `Error Code: ${error.status}`;
    }

    this.updateAuthState({ error: message });
    return throwError(() => error);
  }

  // ==================== Utility Methods ====================

  /**
   * Redirect to login with return URL
   */
  redirectToLogin(returnUrl?: string): void {
    const url = returnUrl || this.router.url;
    this.router.navigate([this.config.logoutRedirectUrl], {
      queryParams: { returnUrl: url }
    });
  }

  /**
   * Redirect after login (to stored return URL or default)
   */
  redirectAfterLogin(returnUrl?: string): void {
    const url = returnUrl || this.config.loginRedirectUrl;
    this.router.navigate([url]);
  }
}

